import React from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
    ScanningOutline,
    CheckCircleOutline,
    TruckOutline,
    EditSOutline,
    SearchOutline
} from "antd-mobile-icons";
import DspLayout from "./layouts/DspLayout";
import {DspPath} from "../../enums/DeliveryNavigatePathEnum";
import {getUserInfo} from "../../utils/auth";
import {useQuery} from "@apollo/client";
import {GET_ACCOUNT_BIND_DSP, GetAccountBindDspResponse} from "./graphql/queries/getAccountBindDsp";
import usePreventEdgeSwipe from "../../hooks/usePreventEdgeSwipe";
import useDspWarehouseStore from "./store/dspWarehouseStore";
import {DspValidationType} from "../../enums/DspValidationType";
import {
    DeliveredIcon,
    DeliveryFailedIcon, DriverViolationIcon,
    PackageSearchIcon,
    PodDocumentIcon,
    SortingIcon,
    TransferOutIcon
} from "../../assets/icons";
import DspWarehouseSelector from "./components/DspWarehouseSelector";

interface FunctionCardProps {
    icon: React.ReactNode;
    title: string;
    onClick: () => void;
    className?: string;
}

const FunctionCard: React.FC<FunctionCardProps> = ({icon, title, onClick, className = ""}) => {
    return (
        <div 
            className={`bg-white p-6 border border-gray-100 flex flex-col items-center justify-center space-y-3 cursor-pointer transition-colors min-h-[120px] ${className}`}
            onClick={onClick}
        >
            <div className="text-5xl" style={{color: '#8B5CF6'}}>
                {icon}
            </div>
            <span className="text-sm font-medium text-center text-gray-700">
                {title}
            </span>
        </div>
    );
};

const DspHomePage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const userInfo = getUserInfo();
    const userName = userInfo?.account.name || 'Kevin';
    const { data } = useQuery<GetAccountBindDspResponse>(GET_ACCOUNT_BIND_DSP);
    const setWarehouses = useDspWarehouseStore((state) => state.setWarehouses);

    React.useEffect(() => {
        if (data?.dsp?.warehouses) {
            setWarehouses(data.dsp.warehouses);
        }
    }, [data?.dsp?.warehouses, setWarehouses]);

    usePreventEdgeSwipe(true, {
        targetPath: DspPath.DELIVERY_DSP_HOME,
        edgeWidth: 15
    });

    const handleReceivingSortingClick = () => {
        navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SCAN);
    };

    const handleTrackPackageClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_TRACK_DETAIL
        });
        
        navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handleDeliveredClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_DELIVERED_POD,
            title: t('pages.dsp.home.delivered'),
            validationType: DspValidationType.SIGN_RECEIPT
        });
        
        navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handleDeliveryAbnormalClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_MARK_EXCEPTION,
            title: t('pages.dsp.home.deliveryFail'),
            validationType: DspValidationType.EXCEPTION_REPORT
        });

        navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handlePodModifyClick = () => {
        const params = new URLSearchParams({
            successPath: DspPath.DELIVERY_DSP_DELIVERED_POD,
            title: t('pages.dsp.home.editPod'),
            validationType: DspValidationType.POD_MODIFY
        });

        navigate(`${DspPath.DELIVERY_DSP_PACKAGE_SCAN}?${params.toString()}`);
    };

    const handlePodReviewClick = () => {
        navigate(DspPath.DELIVERY_DSP_DRIVER_VIOLATION_REVIEW);
    };

    return (
        <DspLayout title={`${t('pages.dsp.home.hello')}, ${userName}`}>
            <div className="min-h-full bg-gradient-to-b from-purple-200 via-purple-100 to-white">
                <div className="px-4 py-2">
                    <div className="text-base font-medium">
                        {t('pages.dsp.home.currentSite')}：<DspWarehouseSelector />
                    </div>
                </div>
                <div className="p-4 space-y-6">
                {/* 库内功能区 */}
                <div className="space-y-4">
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <h2 className="text-base font-medium px-4 pt-4 pb-2">{t('pages.dsp.home.warehouseManagement')}</h2>
                        <div className="grid grid-cols-3">
                            <FunctionCard
                                icon={<SortingIcon className="w-full h-full" width='48' height='48'/>}
                                title={t('pages.dsp.home.sorting')}
                                onClick={handleReceivingSortingClick}
                                className="rounded-none border-0 rounded-tl-2xl rounded-bl-2xl"
                            />
                            <FunctionCard
                                icon={<PackageSearchIcon className="w-full h-full" width='48' height='48'/>}
                                title={t('pages.dsp.home.trackPackage')}
                                onClick={handleTrackPackageClick}
                                className="rounded-none border-0 rounded-tr-2xl rounded-br-2xl"
                            />
                        </div>
                    </div>
                </div>

                {/* 派送功能区 */}
                <div className="space-y-4">
                    <div className="space-y-4">
                        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <h2 className="text-base font-medium px-4 pt-4 pb-2">{t('pages.dsp.home.deliveryManagement')}</h2>
                            <div className="grid grid-cols-3">
                                <FunctionCard
                                    icon={<DeliveredIcon className="w-full h-full" width='48' height='48'/>}
                                    title={t('pages.dsp.home.delivered')}
                                    onClick={handleDeliveredClick}
                                    className="rounded-none border-0 rounded-tl-2xl rounded-bl-2xl"
                                />
                                <FunctionCard
                                    icon={<DeliveryFailedIcon className="w-full h-full" width='48' height='48'/>}
                                    title={t('pages.dsp.home.deliveryFail')}
                                    onClick={handleDeliveryAbnormalClick}
                                    className="rounded-none border-0"
                                />
                                <FunctionCard
                                    icon={<PodDocumentIcon className="w-full h-full" width='48' height='48'/>}
                                    title={t('pages.dsp.home.editPod')}
                                    onClick={handlePodModifyClick}
                                    className="rounded-none border-0 rounded-tr-2xl rounded-br-2xl"
                                />
                                <FunctionCard
                                    icon={<DriverViolationIcon className="w-full h-full" width='48' height='48'/>}
                                    title={t('pages.dsp.home.driverExceptionReview')}
                                    onClick={handlePodReviewClick}
                                    className="rounded-none border-0 rounded-tr-2xl rounded-br-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </DspLayout>
    );
};

export default DspHomePage;


